import React, { useState, useRef, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import uploadQueue from '../../services/uploadQueue';
import { compressImage } from '../../utils/imageUtils';
import FotoPreviewModal from '../common/FotoPreviewModal';

/**
 * Componente de captura y upload de fotos a la galería personal.
 * Estrategia: getUserMedia (cámara trasera) con fallback a file picker.
 * Comprime imágenes a 1920px / 82% JPEG antes de subir.
 *
 * Estados: idle → camara | idle → preview → subiendo → idle
 *
 * @param {Object} props
 * @param {Function} props.onFotoSubida - Callback tras upload exitoso
 * @returns {JSX.Element}
 */
function CapturaFoto({ onFotoSubida, inline = false }) {
  const { showToast } = useToast();
  const [estado, setEstado] = useState('idle'); // idle | camara | preview | subiendo
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);   // con capture="environment" — fallback cámara
  const filePickerRef = useRef(null);  // sin capture — selector de galería/archivos
  const streamRef = useRef(null);

  // Limpiar stream de cámara al desmontar
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const handleDisparar = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const rawFile = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopStream();
      const compressed = await compressImage(rawFile);
      setFile(compressed);
      setPreview(URL.createObjectURL(compressed));
      setEstado('preview');
    }, 'image/jpeg', 0.82);
  };

  const handleFileFromPicker = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const compressed = await compressImage(selectedFile);
    setFile(compressed);
    setPreview(URL.createObjectURL(compressed));
    setEstado('preview');
  };

  const handleCancelarCamara = () => {
    stopStream();
    setEstado('idle');
  };

  const handleVolverACapturar = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setEstado('idle');
  };

  const resetAfterUpload = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setEstado('idle');
  };

  const handleGuardar = async (descripcion) => {
    if (!file) return;
    setEstado('subiendo');
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      if (descripcion) formData.append('descripcion', descripcion);

      const response = await apiService.uploadFoto(formData);
      if (response.success) {
        showToast('Foto subida exitosamente', 'success');
        resetAfterUpload();
        if (onFotoSubida) onFotoSubida(response.data);
      } else {
        showToast(response.message || 'Error al subir', 'error');
        setEstado('preview');
      }
    } catch (error) {
      if (error.message === 'NETWORK_ERROR') {
        try {
          await uploadQueue.add({
            file,
            uploadFn: async (f, meta) => {
              const fd = new FormData();
              fd.append('archivo', f);
              if (meta.descripcion) fd.append('descripcion', meta.descripcion);
              await apiService.uploadFoto(fd);
            },
            metadata: { descripcion: descripcion || null }
          });
        } catch (queueErr) {
          showToast('Error al encolar upload offline', 'error');
          return;
        }
        showToast('Sin conexión. La foto se subirá automáticamente al recuperar la red.', 'warning');
        resetAfterUpload();
      } else {
        showToast(error.message || 'Error al subir foto', 'error');
        setEstado('preview');
      }
    }
  };

  const hiddenInputs = (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handleFileFromPicker} />
      <input ref={filePickerRef} type="file" accept="image/*"
        style={{ display: 'none' }} onChange={handleFileFromPicker} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );

  const previewContent = (
    <>
      {/* Estado: camara */}
      {estado === 'camara' && (
        <div className="captura-camara">
          <video ref={videoRef} className="captura-video-stream" playsInline muted />
          <div className="captura-camara-actions">
            <button className="captura-disparador" onClick={handleDisparar} title="Capturar">
              <i className="fas fa-circle"></i>
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleCancelarCamara}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Estado: subiendo */}
      {estado === 'subiendo' && (
        <div className="captura-subiendo">
          <i className="fas fa-spinner fa-spin fa-2x"></i>
          <p>Subiendo foto…</p>
        </div>
      )}
    </>
  );

  // Modo inline: botón en la barra, preview en modal compartido
  if (inline) {
    return (
      <>
        {hiddenInputs}
        <button className="btn btn-primary" onClick={() => filePickerRef.current?.click()}>
          <i className="fas fa-camera"></i> Agregar foto
        </button>
        <FotoPreviewModal
          isOpen={estado === 'preview' && !!preview}
          previewSrc={preview}
          title="Agregar foto"
          uploading={estado === 'subiendo'}
          onSave={handleGuardar}
          onCancel={handleVolverACapturar}
        />
      </>
    );
  }

  return (
    <div className="captura-foto">
      {hiddenInputs}

      {/* Estado: idle */}
      {estado === 'idle' && (
        <div className="captura-idle">
          <button className="btn btn-primary" onClick={() => filePickerRef.current?.click()}>
            <i className="fas fa-camera"></i> Agregar foto
          </button>
        </div>
      )}

      {previewContent}

      <FotoPreviewModal
        isOpen={estado === 'preview' && !!preview}
        previewSrc={preview}
        title="Agregar foto"
        uploading={estado === 'subiendo'}
        onSave={handleGuardar}
        onCancel={handleVolverACapturar}
      />
    </div>
  );
}

export default CapturaFoto;
